import {
  Alert,
  Box,
  CircularProgress,
  Container,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRowsProp,
} from "@mui/x-data-grid";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import { useWhitelabel } from "../../context/WhitelabelContext";
import { hostname, parseError } from "../../utils";

interface PublicListHeader {
  id: string;
  name: string;
  type?: string;
  dataType?: string;
  order?: number;
}

interface PublicListMeta {
  id: string;
  name: string;
  type?: string;
  headers: PublicListHeader[];
}

interface PublicRowsResponse {
  headers: PublicListHeader[];
  rows: {
    items: Record<string, any>[];
    total: number;
    page: number;
    pages: number;
    has_next: boolean;
  };
}

const DEFAULT_PAGE_SIZE = 50;

export default ({ mode }: { mode: string }) => {
  const { listId } = useParams();
  const { whitelabelSettings } = useWhitelabel();

  const [meta, setMeta] = useState<PublicListMeta | null>(null);
  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Debounce the search input so we don't fire a request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Reset to first page when the search changes.
  useEffect(() => {
    setPaginationModel((m) => ({ ...m, page: 0 }));
  }, [debouncedSearch]);

  useEffect(() => {
    if (!listId) return;
    let cancelled = false;
    const loadMeta = async () => {
      try {
        const res = await axios.get<{ list: PublicListMeta }>(
          `${hostname}/public/lists/${listId}`
        );
        if (!cancelled) {
          setMeta(res.data.list);
        }
      } catch (err: any) {
        if (!cancelled) {
          if (err?.response?.status === 404) {
            setError("This list is not available.");
          } else {
            setError(parseError(err));
          }
          setLoading(false);
        }
      }
    };
    loadMeta();
    return () => {
      cancelled = true;
    };
  }, [listId]);

  useEffect(() => {
    if (!listId || !meta) return;
    let cancelled = false;
    const loadRows = async () => {
      setLoading(true);
      try {
        const res = await axios.get<PublicRowsResponse>(
          `${hostname}/public/lists/${listId}/rows`,
          {
            params: {
              page: paginationModel.page + 1,
              pageSize: paginationModel.pageSize,
              ...(debouncedSearch ? { searchQuery: debouncedSearch } : {}),
            },
          }
        );
        if (!cancelled) {
          setRows(res.data.rows.items || []);
          setRowCount(res.data.rows.total || 0);
        }
      } catch (err: any) {
        if (!cancelled) {
          if (err?.response?.status === 429) {
            setError("Too many requests. Please slow down and try again.");
          } else {
            setError(parseError(err));
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadRows();
    return () => {
      cancelled = true;
    };
  }, [listId, meta, paginationModel.page, paginationModel.pageSize, debouncedSearch]);

  const columns: GridColDef[] = useMemo(() => {
    if (!meta) return [];
    // Sort headers by `order` so columns match the owner's view.
    const ordered = [...meta.headers].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    return ordered.map((h) => ({
      field: h.name,
      headerName: h.name,
      flex: 1,
      minWidth: 150,
      sortable: false,
      filterable: false,
    }));
  }, [meta]);

  const gridRows: GridRowsProp = useMemo(
    () => rows.map((r) => ({ id: r.rowId, ...r })),
    [rows]
  );

  const platformName = whitelabelSettings?.title || "GTM AI";

  return (
    <Box>
      <Header mode={mode} maxWidth={"xl"} />
      <Container maxWidth="xl" sx={{ mt: 3 }}>
        {meta && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h4">{meta.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              A public list shared via {platformName}.
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        )}

        {!error && (
          <>
            <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
              <TextField
                size="small"
                placeholder="Search…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ minWidth: 240 }}
                disabled={!meta}
              />
            </Box>
            <Box sx={{ height: 600, width: "100%" }}>
              {!meta && loading ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <DataGrid
                  rows={gridRows}
                  columns={columns}
                  loading={loading}
                  paginationMode="server"
                  rowCount={rowCount}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  pageSizeOptions={[25, 50, 100]}
                  disableRowSelectionOnClick
                  disableColumnMenu
                />
              )}
            </Box>
          </>
        )}
      </Container>
      <Footer />
    </Box>
  );
};
